import React, { useCallback, useEffect, useState } from "react";
import { api, exportCsv, formatApiError, fmtQty } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useStockItems } from "@/lib/useStockItems";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BulkButtons } from "@/components/common/BulkUpload";
import { Plus, Pencil, Trash2, PackageCheck, Download, ShoppingCart, History } from "lucide-react";
import { PODialog, newPO } from "@/components/purchasing/PODialog";
import { POReceiveDialog } from "@/components/purchasing/POReceiveDialog";
import { POTraceDialog } from "@/components/purchasing/POTraceDialog";
import { POStatus } from "@/components/purchasing/POStatus";
import { DataTable } from "@/components/common/DataTable";
import { KindBadge, ItemCell, d10 } from "@/components/common/cells";
import { toast } from "sonner";

const tabCls = "data-[state=active]:bg-slate-900 data-[state=active]:text-white";
const rowKey = (r) => `${r.kind}-${r.item_id}`;

function ShortageTable({ report, selected, setSelected, onCreatePO, canEdit }) {
  const { t } = useLang();
  const allOn = report.length > 0 && report.every((r) => selected.has(rowKey(r)));
  const toggle = (k) => { const s = new Set(selected); s.has(k) ? s.delete(k) : s.add(k); setSelected(s); };
  const columns = [
    { key: "select", filter: false, sort: false,
      label: canEdit ? <Checkbox checked={allOn} onCheckedChange={(v) => setSelected(v ? new Set(report.map(rowKey)) : new Set())} data-testid="shortage-select-all" /> : "",
      render: (r) => canEdit && <Checkbox checked={selected.has(rowKey(r))} onCheckedChange={() => toggle(rowKey(r))} data-testid={`shortage-select-${r.code}`} /> },
    { key: "kind", label: t("kind"), value: (r) => String(r.kind).toUpperCase(), render: (r) => <KindBadge kind={r.kind} /> },
    { key: "item", label: t("item"), value: (r) => `${r.code} ${r.name}`, render: (r) => <ItemCell code={r.code} name={r.name} /> },
    { key: "stock", label: t("stock"), align: "right", className: "font-mono", render: (r) => fmtQty(r.stock) },
    { key: "allocated", label: t("allocated"), align: "right", className: "font-mono", render: (r) => fmtQty(r.allocated) },
    { key: "incoming", label: t("incoming"), align: "right", className: "font-mono text-sky-700", render: (r) => fmtQty(r.incoming) },
    { key: "to_buy", label: t("to_buy"), align: "right", className: "font-mono font-semibold text-red-700", render: (r) => fmtQty(r.to_buy) },
    { key: "needed_by", label: t("needed_by"), className: "font-mono text-xs whitespace-nowrap", value: (r) => d10(r.needed_by) },
    { key: "vendor", label: t("vendor") },
    { key: "actions", label: "", filter: false, sort: false, render: (r) => canEdit && (
      <Button size="sm" variant="outline" onClick={() => onCreatePO([r])} data-testid={`shortage-po-${r.code}`}><Plus size={12} className="mr-1" />PO</Button>
    ) },
  ];
  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-2 items-center justify-between">
          <h3 className="text-lg font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("purchasing_report")}</h3>
          <div className="flex gap-2">
            {canEdit && (
              <Button size="sm" disabled={selected.size === 0} onClick={() => onCreatePO(report.filter((r) => selected.has(rowKey(r))))}
                      className="bg-slate-900 hover:bg-slate-800" data-testid="create-po-selected-btn">
                <ShoppingCart size={14} className="mr-2" />{t("create_po_selected")} ({selected.size})
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => exportCsv("purchasing")} data-testid="purchasing-export-btn">
              <Download size={14} className="mr-2" />{t("export_excel")}
            </Button>
          </div>
        </div>
        <DataTable columns={columns} rows={report} rowKey={rowKey} testid="purchasing-report-table" />
      </CardContent>
    </Card>
  );
}

function ShipmentShortageTable({ rows }) {
  const { t } = useLang();
  const num = (key, label, cls = "") => ({ key, label: t(label), align: "right", className: `font-mono ${cls}`, render: (r) => fmtQty(r[key]) });
  const columns = [
    { key: "plan_code", label: t("plan_code"), className: "font-mono text-xs whitespace-nowrap" },
    { key: "stuffing_date", label: t("stuffing_date"), className: "font-mono text-xs whitespace-nowrap", value: (r) => d10(r.stuffing_date) },
    { key: "buyer", label: t("buyers"), className: "whitespace-nowrap" },
    { key: "product", label: t("products"), className: "whitespace-nowrap" },
    { key: "packing_style", label: t("packing_styles"), className: "whitespace-nowrap" },
    { key: "kind", label: t("kind"), value: (r) => String(r.kind).toUpperCase(), render: (r) => <KindBadge kind={r.kind} /> },
    { key: "item", label: t("item"), value: (r) => `${r.code} ${r.name}`, render: (r) => <ItemCell code={r.code} name={r.name} /> },
    num("required", "required"), num("from_stock", "from_stock", "text-emerald-700"), num("shortage", "shortage", "text-amber-700"),
    num("from_po", "from_po", "text-sky-700"), num("to_buy", "to_buy", "font-semibold text-red-700"),
    { key: "needed_by", label: t("needed_by"), className: "font-mono text-xs whitespace-nowrap", value: (r) => d10(r.needed_by) },
  ];
  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-2 items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("shipment_shortage")}</h3>
            <p className="text-xs text-slate-500">{t("alloc_hint")}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => exportCsv("shipment-shortage")} data-testid="shipment-shortage-export-btn">
            <Download size={14} className="mr-2" />{t("export_excel")}
          </Button>
        </div>
        <DataTable columns={columns} rows={rows} rowKey={(r) => `${r.id}-${r.line_id}-${r.kind}`} testid="shipment-shortage-table" />
      </CardContent>
    </Card>
  );
}

function POTable({ pos, lookup, onAdd, onEdit, onReceive, onDelete, onTrace, canEdit, canReceive, onReload }) {
  const { t } = useLang();
  const linesText = (p) => p.lines.map((l) => { const it = lookup(l.item_type, l.item_id); return `${it.code} ${it.name}`; }).join(" ");
  const columns = [
    { key: "po_number", label: t("po_number"), className: "font-mono text-xs whitespace-nowrap" },
    { key: "vendor", label: t("vendor") },
    { key: "order_date", label: t("order_date"), className: "font-mono text-xs whitespace-nowrap", value: (p) => d10(p.order_date) },
    { key: "expected_date", label: t("expected_date"), className: "font-mono text-xs whitespace-nowrap", value: (p) => d10(p.expected_date) },
    { key: "lines", label: t("items_label"), value: linesText, className: "min-w-[260px]", render: (p) => (
      <div className="space-y-1">
        {p.lines.map((l) => { const it = lookup(l.item_type, l.item_id); return (
          <div key={l.line_id} className="flex justify-between gap-4 text-xs">
            <span><span className="font-mono text-slate-500">{it.code}</span> {it.name}</span>
            <span className="font-mono whitespace-nowrap"><span className="text-emerald-700">{fmtQty(l.received_qty)}</span> / {fmtQty(l.qty)}</span>
          </div>
        ); })}
      </div>
    ) },
    { key: "status", label: t("status"), align: "center", render: (p) => <POStatus status={p.status} /> },
    { key: "actions", label: "", filter: false, sort: false, render: (p) => {
      const done = ["received", "cancelled"].includes(p.status);
      return (
        <div className="flex justify-end whitespace-nowrap">
          <Button variant="ghost" size="sm" onClick={() => onTrace(p)} title={t("po_trace")} data-testid={`po-trace-${p.po_number}`}><History size={14} /></Button>
          {canReceive && <Button variant="ghost" size="sm" disabled={done} onClick={() => onReceive(p)} title={t("receive_po")} data-testid={`po-receive-${p.po_number}`}><PackageCheck size={14} /></Button>}
          {canEdit && <>
            <Button variant="ghost" size="sm" onClick={() => onEdit(p)} data-testid={`po-edit-${p.po_number}`}><Pencil size={14} /></Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(p)} className="text-red-600" data-testid={`po-delete-${p.po_number}`}><Trash2 size={14} /></Button>
          </>}
        </div>
      );
    } },
  ];
  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-2 items-center justify-between">
          <h3 className="text-lg font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Purchase Orders</h3>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => exportCsv("outstanding-po")} data-testid="po-export-btn"><Download size={14} className="mr-2" />{t("export_excel")}</Button>
            {canEdit && <BulkButtons kind="pos" title="PO" onDone={onReload} testid="po-bulk" />}
            {canEdit && <Button size="sm" onClick={onAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="po-add-btn"><Plus size={14} className="mr-2" />{t("add")}</Button>}
          </div>
        </div>
        <DataTable columns={columns} rows={pos} testid="pos-table" />
      </CardContent>
    </Card>
  );
}

export default function Purchasing() {
  const { t } = useLang();
  const { can } = useAuth();
  const canEdit = can("purchasing", "edit");
  const { items, lookup, reload: reloadItems } = useStockItems();
  const [pos, setPos] = useState([]);
  const [report, setReport] = useState([]);
  const [shipShort, setShipShort] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [poInitial, setPoInitial] = useState(null);
  const [receivePo, setReceivePo] = useState(null);
  const [traceId, setTraceId] = useState(null);
  const [tab, setTab] = useState("report");

  const load = useCallback(async () => {
    const [p, r, ss] = await Promise.all([api.get("/pos"), api.get("/reports/purchasing"), api.get("/reports/shipment-shortage")]);
    setPos(p.data);
    setShipShort(ss.data);
    setReport(r.data);
    setSelected(new Set());
  }, []);
  useEffect(() => { load(); }, [load]);

  const createFromShortage = (rows) => {
    const needed = rows.map((r) => r.needed_by).filter(Boolean).sort()[0];
    setPoInitial(newPO(
      rows.map((r) => ({ item_type: r.kind, item_id: r.item_id, qty: Math.ceil(r.to_buy) })),
      { vendor: rows.find((r) => r.vendor)?.vendor || "", expected_date: needed ? needed.slice(0, 10) : "" },
    ));
  };

  const remove = async (p) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/pos/${p.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="purchasing-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("purchasing")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("po_recommendation")}</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="report" className={tabCls} data-testid="tab-purchasing-report">{t("purchasing_report")} ({report.length})</TabsTrigger>
          <TabsTrigger value="ship-shortage" className={tabCls} data-testid="tab-shipment-shortage">{t("shipment_shortage")} ({shipShort.length})</TabsTrigger>
          <TabsTrigger value="pos" className={tabCls} data-testid="tab-pos">PO ({pos.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="report" className="mt-4">
          <ShortageTable report={report} selected={selected} setSelected={setSelected} onCreatePO={createFromShortage} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="ship-shortage" className="mt-4">
          <ShipmentShortageTable rows={shipShort} />
        </TabsContent>
        <TabsContent value="pos" className="mt-4">
          <POTable pos={pos} lookup={lookup} onAdd={() => setPoInitial(newPO())} onEdit={setPoInitial} onReceive={setReceivePo} onDelete={remove} onTrace={(p) => setTraceId(p.id)} canEdit={canEdit} canReceive={canEdit || can("stock", "edit")} onReload={load} />
        </TabsContent>
      </Tabs>
      <PODialog initial={poInitial} onClose={() => setPoInitial(null)} items={items} onSaved={() => { load(); setTab("pos"); }} />
      <POReceiveDialog po={receivePo} onClose={() => setReceivePo(null)} items={items} onSaved={() => { load(); reloadItems(); }} />
      <POTraceDialog poId={traceId} onClose={() => setTraceId(null)} />
    </div>
  );
}
